/**
 * @file Reduce-motion preference — localStorage flag + `<html>` attribute.
 * @module @labcraft/lab/ui/lib/reduce-motion
 *
 * The Settings modal's "Reduce motion" toggle persists a learner choice
 * that augments the OS `prefers-reduced-motion` media query: when set,
 * the new chrome (app bar / avatar dropdown / settings modal) suppresses
 * its entrance animations regardless of the
 * OS setting. CSS gates on `[data-reduce-motion='true']` on the root
 * element; this module is the read/write/apply surface for that
 * attribute + its persisted backing.
 *
 * {@link applyStoredReduceMotion} runs once at boot (from `main.tsx`) so
 * a learner who opted in keeps the quiet experience across reloads
 * before any React renders. Every accessor takes the `Storage`
 * explicitly (defaulting to `localStorage`) so the logic is unit-
 * testable against an in-memory stand-in.
 */

/** localStorage key for the reduce-motion preference (`'true'`/absent). */
export const REDUCE_MOTION_KEY = 'labcraft.reduceMotion'

/** Root-element attribute CSS gates on. Present + `'true'` ⇒ reduced. */
const REDUCE_MOTION_ATTR = 'data-reduce-motion'

/**
 * Resolve the ambient `Storage`. Returns the passed store, else
 * `localStorage`, else `null` (SSR / private mode / storage disabled).
 * A `null` store means "can't persist" — the toggle still works for the
 * session via the `<html>` attribute, it just won't survive a reload.
 *
 * @param store - Explicit `Storage`; omitted in the browser.
 * @returns The resolved `Storage`, or `null`.
 */
function resolveStore(store?: Storage): Storage | null {
  if (store) return store
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/**
 * Whether the learner has opted into reduced motion. Defaults to
 * `false` (motion on) when storage is unavailable — the OS media query
 * still applies on top, so a learner with `prefers-reduced-motion` set
 * at the OS level is unaffected by this default.
 *
 * @param store - Explicit `Storage`; omitted in the browser.
 * @returns `true` when the toggle is on.
 */
export function readReduceMotion(store?: Storage): boolean {
  const s = resolveStore(store)
  if (!s) return false
  try {
    return s.getItem(REDUCE_MOTION_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Persist the reduce-motion preference and reflect it onto the root
 * `<html>` element so CSS picks it up immediately. Setting `false`
 * removes both the stored value and the attribute (the OS media query
 * is the floor).
 *
 * @param on - The new preference.
 * @param store - Explicit `Storage`; omitted in the browser.
 */
export function writeReduceMotion(on: boolean, store?: Storage): void {
  const s = resolveStore(store)
  try {
    if (on) s?.setItem(REDUCE_MOTION_KEY, 'true')
    else s?.removeItem(REDUCE_MOTION_KEY)
  } catch {
    // Best-effort — the attribute below still drives the live session.
  }
  if (typeof document !== 'undefined') {
    if (on) document.documentElement.setAttribute(REDUCE_MOTION_ATTR, 'true')
    else document.documentElement.removeAttribute(REDUCE_MOTION_ATTR)
  }
}

/**
 * Apply the persisted reduce-motion preference to `<html>` at boot,
 * before React mounts, so an opted-in learner never sees a flash of
 * the animated chrome on reload. No-op when the preference is unset.
 *
 * @param store - Explicit `Storage`; omitted in the browser.
 */
export function applyStoredReduceMotion(store?: Storage): void {
  if (readReduceMotion(store) && typeof document !== 'undefined') {
    document.documentElement.setAttribute(REDUCE_MOTION_ATTR, 'true')
  }
}
